// src/services/data.js
// All read endpoints go through /api/rprsData?type=xxx (Azure Function proxy).
// Logic App URLs are stored server-side in environment variables — never shipped to the browser.

const DATA_BASE = '/api/rprsData';

async function jsonOrThrow(res, label) {
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`${label} fetch failed: ${res.status} ${res.statusText} ${text?.slice(0, 120)}`);
  }
  return res.json();
}

async function fetchData(type, label, body = {}) {
  const url = `${DATA_BASE}?type=${encodeURIComponent(type)}`;
  const r = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return jsonOrThrow(r, label);
}

export async function fetchDetail() {
  const data = await fetchData('detail', 'detail');

  if (!Array.isArray(data)) {
    console.warn('Detail blob is not an array; got:', data);
    return [];
  }

  if (data.length) {
    console.log('[RPRS] detail[0]:', data[0]);
  } else {
    console.log('[RPRS] detail: empty array');
  }

  return data;
}

export async function fetchEmployees() {
  const data = await fetchData('employee', 'employee');

  if (!Array.isArray(data)) {
    console.warn('Employee blob is not an array; got:', data);
    return [];
  }

  if (data.length) {
    console.log('[RPRS] employee[0]:', data[0]);
  } else {
    console.log('[RPRS] employee: empty array');
  }

  return data;
}

export async function fetchMetadata() {
  // Read workflow metadata directly from rprs.WorkflowEntries (SQL)
  // instead of via LA → Snowflake blob staging
  const WF_ENDPOINT = '/api/rprsWorkflow';
  const res = await fetch(`${WF_ENDPOINT}?action=read`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  });
  const data = await jsonOrThrow(res, 'metadata');

  if (!Array.isArray(data)) {
    console.warn('Metadata blob is not an array; got:', data);
    return [];
  }

  if (data.length) {
    console.log('[RPRS] metadata[0]:', data[0]);
  } else {
    console.log('[RPRS] metadata: empty array');
  }

  if (data.length) {
    console.log("[RPRS] metadata keys sample:",
      data.slice(0, 10).map(x => x.KEY ?? x.key ?? null)
    );
  }
  if (data.length) {
    console.log("[RPRS] metadata keys:",
      data.map(x => x.KEY ?? x.key ?? null)
    );
  }

  return data;
}

/** Fire-and-forget: purge stale elective/excel-import workflow entries. */
export function triggerCleanup() {
  fetch('/api/rprsWorkflow?action=cleanup', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  }).catch(err => console.warn('[RPRS] Cleanup trigger failed (non-blocking):', err));
}

export async function deleteTask(key) {
  const res = await fetch('/api/rprsWorkflow?action=delete', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ key }),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Delete task failed: ${res.status} ${res.statusText} ${text.slice(0, 120)}`);
  }
  return res.json().catch(() => ({}));
}

export async function fetchWIP() {
  const data = await fetchData('wip_individual', 'wip');

  if (!Array.isArray(data)) {
    console.warn('WIP blob is not an array; got:', data);
    return [];
  }

  if (data.length) {
    console.log('[RPRS] wip[0]:', data[0]);
  } else {
    console.log('[RPRS] wip: empty array');
  }

  return data;
}

/**
 * Fetch active SP/SM service assignments for the given client codes.
 * @param {string[]} clientCodes - array of client codes to filter on
 * @returns {Promise<Array>} rows with ContIndex, ClientCode, ClientName, ServIndex, etc.
 */
export async function fetchActiveServices(clientCodes) {
  const data = await fetchData('active_services', 'active_services', { clientCodes });
  if (!Array.isArray(data)) {
    console.warn('[RPRS] active_services: unexpected response', data);
    return [];
  }
  if (data.length) {
    console.log('[RPRS] active_services[0]:', data[0]);
  }
  return data;
}

/**
 * Fetch client relationship group data (parent/child mappings).
 * @returns {Promise<Array<{clientCode:string, parentCode:string}>>}
 */
export async function fetchClientGroups() {
  const data = await fetchData('client_groups', 'client_groups');
  if (!Array.isArray(data)) {
    console.warn('[RPRS] client_groups: unexpected response', data);
    return [];
  }
  return data;
}

/**
 * Fetch all client/role/service assignments for a given staff member.
 * @param {number} staffIndex - the staff index to look up
 * @returns {Promise<Array<{ClientCode:string, ClientName:string, Role:string, ServIndex:string, ContIndex:number}>>}
 */
export async function fetchStaffAssignments(staffIndex) {
  const data = await fetchData('staff_assignments', 'staff_assignments', { staffIndex });
  if (!Array.isArray(data)) {
    console.warn('[RPRS] staff_assignments: unexpected response', data);
    return [];
  }
  if (data.length) {
    console.log('[RPRS] staff_assignments[0]:', data[0]);
  }
  return data;
}
